import React, { useEffect, useState } from "react";
import { Modal, Button, Icon } from "antd";
import DownloadingStep from "../../ReportModalSteps/DownloadingStep";
import {
  getExportMaxLimit,
  ListExportScope,
} from "../../../utils/listExportMaxConfig";
import "./ListExportModal.scss";

/**
 * Modal genérico de exportación para listados.
 *
 * @param {object} props
 * @param {object} props.listSummary - Objeto con totalResults del listado
 * @param {object} props.exportState - Estado de exportación { visible, fileType, ...extras }
 * @param {function} props.onClose - Callback para cerrar el modal
 * @param {function} props.onRequestData - Callback async para solicitar datos (fileType, maxExport) => Promise<{statusCode, data}>
 * @param {function} props.onExport - Callback para ejecutar la exportación (data, fileType) => void
 * @param {string} props.exportScope - Identificador del scope (ListExportScope.SHIPMENT_LIST | ListExportScope.ALARMS)
 */
export default function ListExportModal({
  listSummary,
  exportState,
  onClose,
  onRequestData,
  onExport,
  exportScope = ListExportScope.SHIPMENT_LIST,
}) {
  const { visible, fileType } = exportState;
  const [step, setStep] = useState("downloading");

  const maxExport = getExportMaxLimit(exportScope);

  const reset = () => {
    setStep("downloading");
  };

  const handleCancel = () => {
    onClose();
    reset();
  };

  const handleConfirm = async () => {
    setStep("downloading");

    const res = await onRequestData(fileType, maxExport);

    if (!res || res.statusCode !== 200) {
      setStep("error");
      return;
    }

    onExport(res.data || [], fileType);
    onClose();
    reset();
  };

  useEffect(() => {
    if (!visible) {
      reset();
      return;
    }

    let mounted = true;

    (async () => {
      const totalResults = Number(listSummary?.totalResults || 0);

      if (totalResults > maxExport) {
        if (mounted) setStep("limit");
        return;
      }

      setStep("downloading");
      const res = await onRequestData(fileType, maxExport);

      if (!mounted) return;

      if (!res || res.statusCode !== 200) {
        setStep("error");
        return;
      }

      onExport(res.data || [], fileType);
      onClose();
      reset();
    })();

    return () => {
      mounted = false;
    };
  }, [
    visible,
    fileType,
    maxExport,
    onRequestData,
    onExport,
    onClose,
    listSummary?.totalResults,
  ]);

  return (
    <Modal
      visible={visible}
      footer={null}
      centered
      maskClosable={false}
      closable={false}
      destroyOnClose
      className="ListExportModal"
    >
      {step === "downloading" && <DownloadingStep allowCancel={false} />}

      {step === "limit" && (
        <div className="limit-content">
          <div className="limit-title">
            <Icon
              type="exclamation-circle"
              theme="twoTone"
              twoToneColor="#FAAD14"
              className="exclamation-icon"
            />
            <h3>Límite de resultados superado</h3>
          </div>

          <p className="limit-text">
            El número total de resultados <b>({listSummary?.totalResults})</b>{" "}
            supera el máximo establecido para exportar los datos. Pulse
            Confirmar para obtener los primeros <b>{maxExport}</b> registros o
            realice una nueva búsqueda acotada.
          </p>

          <div className="footer-buttons">
            <Button className="cancel-btn" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button
              type="primary"
              className="confirm-btn"
              onClick={handleConfirm}
            >
              Confirmar
            </Button>
          </div>
        </div>
      )}

      {step === "error" && (
        <div className="error-content">
          <div className="error-title">
            <Icon
              type="close-circle"
              theme="twoTone"
              twoToneColor="#FF4D4F"
              className="error-icon"
            />
            <h3>Error en la exportación</h3>
          </div>

          <p className="error-text">
            Se ha producido un error al obtener los datos para la exportación.
            Por favor, inténtelo de nuevo más tarde.
          </p>

          <div className="footer-buttons">
            <Button type="primary" onClick={handleCancel}>
              Cerrar
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export { ListExportScope };
